<template>
  <div v-if="isHoldingsTransfer">
    <holdings-history-page />
  </div>
  <section v-else class="pt-8 px-8">
    <div>
      <!-- transaction details -->
      <div class="flex flex-col items-center py-2 justify-center gap-y-2">
        <h3 class="text-2xl font-semibold mt-1">
          {{
            transactionInfo.depositMethod === $transactionType.ASSET_TRANSFER
              ? parseMoneyAll(
                  transactionInfo.transferAmount.amount +
                    (transactionInfo.businessCommission || 0),
                  transactionInfo.transferAmount.currency
                )
              : transactionInfo.depositMethod ===
                $transactionType.LOCAL_TRANSFER
              ? parseMoneyAll(
                  transactionInfo.transferAmount.amount +
                    (transactionInfo.businessCommission || 0),
                  transactionInfo.transferAmount.currency
                )
              : parseMoneyAll(
                  (transactionInfo.transferAmount.amount ||
                    transactionDetails.amount) +
                    (transactionInfo.businessCommission || 0),
                  transactionInfo.transferAmount.currency
                )
          }}
        </h3>
        <p class="text-sm font-normal">
          {{
            transactionInfo.depositMethod === $transactionType.LOCAL_TRANSFER
              ? transactionInfo.localCurrencyDepositAmount.currency
              : transactionInfo.depositMethod ===
                $transactionType.ASSET_TRANSFER
              ? transactionInfo.assetDepositAmount.currency
              : transactionInfo.depositAmount.currency
          }}
          {{
            addComma(
              (
                (transactionInfo.depositMethod ===
                $transactionType.LOCAL_TRANSFER
                  ? transactionInfo.localCurrencyDepositAmount.amount
                  : transactionInfo.depositMethod ===
                    $transactionType.ASSET_TRANSFER
                  ? transactionInfo.assetDepositAmount.amount
                  : transactionInfo.depositMethod ===
                    $transactionType.WIRE_TRANSFER
                  ? transactionInfo.fxCurrencyDepositAmount.amount
                  : '-') - transactionInfo.transferFee.amount
              ).toFixed(2)
            )
          }}
        </p>
        <p
          class="text-xs text-center font-normal rounded-md px-1 py-2 capitalize w-24 mt-3 mb-2"
          :class="statusClass(transactionInfo.transferStatus)"
        >
          {{ transactionInfo.transferStatus }}
        </p>
      </div>
    </div>
    <div class="text-fg-muted">
      <ul>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 lg:py-6"
        >
          <span>Time & Date</span>
          <span class="">{{ displayTimeDate }}</span>
        </li>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 lg:py-6"
        >
          <span>Transaction ID</span>
          <span
            >{{ maskID(transactionInfo.transferID) }}
            <span
              class="cursor-pointer mb-1 ml-2"
              v-clipboard:copy="transactionInfo.transferID"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError"
            >
              <copy-icon />
            </span>
          </span>
        </li>
        <li
          class="grid grid-cols-2 items-center text-sm font-normal border-b py-3.5 lg:py-6"
        >
          <span>Payment Method</span>
          <span class="flex items-center gap-x-2">
            <span class="capitalize">{{
              formatServerName(transactionInfo.depositMethod)
            }}</span>
          </span>
        </li>
        <li
          class="flex justify-between font-normal text-sm items-center border-b py-3.5 lg:py-5"
        >
          <span>Reason For Transfer</span>
          <span
            class="text-primary font-semibold text-2xl cursor-pointer"
            @click="toggleShow('showReasonForTransfer')"
            >{{ showReasonForTransfer ? '-' : '+' }}</span
          >
        </li>
        <transition name="slide-fade">
          <div v-if="showReasonForTransfer">
            <li
              class="items-center text-sm border rounded-lg font-normal border-b py-3.5 lg:py-4 my-3 bg-netural px-3"
            >
              <span style="word-break: normal">{{
                transactionInfo.transferReason
              }}</span>
            </li>
          </div>
        </transition>
        <li
          class="grid grid-cols-2 items-center font-normal text-sm border-b py-3.5 lg:py-6"
        >
          <span>From</span>
          <span class="flex items-center gap-x-2">
            <currency-icons
              :currency="
                getCurrencyCountry(transactionInfo.transferInstruction.from)
              "
            />
            <span class="capitalize">{{
              transactionInfo.transferInstruction.from
            }}</span>
          </span>
        </li>
        <li
          class="grid grid-cols-2 items-center border-b font-normal text-sm py-3.5 lg:py-6"
        >
          <span>To</span>
          <span class="flex items-center gap-x-2">
            <currency-icons
              :currency="
                getCurrencyCountry(transactionInfo.transferInstruction.to)
              "
            />
            <span class="capitalize">{{
              transactionInfo.transferInstruction.to
            }}</span>
          </span>
        </li>
        <li
          class="flex justify-between font-normal text-sm items-center py-3.5 lg:py-5"
        >
          <span>Deposit Details</span>
          <span
            class="text-primary font-semibold text-2xl cursor-pointer"
            @click="toggleShow('showDepositDetails')"
            >{{ showDepositDetails ? '-' : '+' }}</span
          >
        </li>
        <transition name="slide-fade">
          <div v-if="showDepositDetails">
            <!-- local deposit details -->
            <div class="pt-3" v-if="isLocalTransferDetails">
              <div
                v-for="(details, index) in localDepositDetails"
                :key="index"
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <template v-if="valueExists(details.value)">
                  <p class="text-sm font-thin">{{ details.label }}</p>
                  <p class="text-sm font-semibold capitalize truncate">
                    {{ details.value }}
                    <span
                      class="cursor-pointer mb-1 ml-2"
                      v-clipboard:copy="details.value"
                      v-clipboard:success="onCopy"
                      v-clipboard:error="onError"
                    >
                      <copy-icon />
                    </span>
                  </p>
                </template>
              </div>
            </div>
            <!-- fx deposit details wire instruction and wire instruction international -->
            <div class="pt-3" v-else>
              <div
                class="flex items-center gap-x-2 font-normal text-xs lg:text-sm mb-5"
              >
                <span>Wire Transfer</span>
                <el-switch v-model="fxDepositDetailsType" class="ml-2" />
                <span>Wire Transfer International</span>
              </div>
              <div
                v-for="(details, index) in fxTransferDepositDetails"
                :key="index"
                class="grid grid-cols-2 border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">{{ details.label }}</p>
                <p class="text-sm font-semibold capitalize overflow-scroll">
                  {{ details.value }}
                  <span
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="details.value"
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </span>
                </p>
              </div>
            </div>
          </div>
        </transition>
        <li
          class="grid grid-cols-4 items-center text-sm font-normal rounded-md border-t py-3.5 lg:py-6"
        >
          <span class="col-span-1">Rate Change : </span>
          <span>
            <el-tooltip
              class="item break-words"
              content="We notify you if there has been a change in rate that was initially"
              placement="top"
              effect="light"
            >
              <warning-icon class="h-4 col-span-1" />
            </el-tooltip>
          </span>
          <span class="flex items-center text-fg-default col-span-2 gap-x-2">
            <span class="capitalize font-semibold">{{
              rateIncreased ? 'yes' : 'no'
            }}</span>
          </span>
        </li>
        <li
          class="flex justify-between font-normal text-sm items-center rounded-md border-t py-3.5 lg:py-5"
        >
          <span>Exchange Details</span>
          <span
            class="text-primary font-semibold text-2xl cursor-pointer"
            @click="toggleShow('showExchangeDetails')"
            >{{ showExchangeDetails ? '-' : '+' }}</span
          >
        </li>
        <transition name="slide-fade">
          <div v-if="showExchangeDetails">
            <div class="pt-3">
              <div
                class="flex flex-row rounded-md border py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Amount converted</p>
                <p class="text-sm font-semibold">
                  {{
                    transactionInfo.depositMethod ===
                    $transactionType.LOCAL_TRANSFER
                      ? transactionInfo.localCurrencyDepositAmount.currency
                      : transactionInfo.depositMethod ===
                        $transactionType.ASSET_TRANSFER
                      ? transactionInfo.assetDepositAmount.currency
                      : transactionInfo.depositAmount.currency
                  }}
                  {{
                    addComma(
                      (
                        (transactionInfo.depositMethod ===
                        $transactionType.LOCAL_TRANSFER
                          ? transactionInfo.localCurrencyDepositAmount.amount
                          : transactionInfo.depositMethod ===
                            $transactionType.ASSET_TRANSFER
                          ? transactionInfo.assetDepositAmount.amount
                          : transactionInfo.depositMethod ===
                            $transactionType.WIRE_TRANSFER
                          ? transactionInfo.fxCurrencyDepositAmount.amount
                          : '-') - transactionInfo.transferFee.amount
                      ).toFixed(2)
                    )
                  }}
                </p>
              </div>
              <div
                class="flex flex-row rounded-md border py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Exchange rate</p>
                <p class="text-sm font-semibold">
                  {{
                    transactionInfo.transferInstruction.division_operator
                      ? transactionInfo.transferInstruction.from
                      : transactionInfo.transferInstruction.to
                  }}
                  {{ transactionInfo.transferInstruction.exchange_rate }}
                </p>
              </div>
              <div
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">
                  Our fees {{ transactionInfo.percentageFees || ''
                  }}{{ transactionInfo.percentageFees ? '%' : '' }}
                </p>
                <p class="text-sm font-semibold">
                  {{
                    transactionInfo.transferFeePercentage
                      ? `${transactionInfo.transferFeePercentage}%`
                      : ''
                  }}
                </p>
              </div>
              <div
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">
                  <span
                    v-if="transactionInfo.transferDestination.personalDetail"
                  >
                    {{ transactionInfo.transferDestination.personalDetail }}
                  </span>
                  <span v-else>Recipient</span>
                  gets
                </p>
                <p class="text-sm font-bold">
                  {{
                    parseMoneyAll(
                      transactionInfo.transferAmount.amount,
                      transactionInfo.transferAmount.currency
                    )
                  }}
                </p>
              </div>
            </div>
          </div>
        </transition>
        <li
          class="flex justify-between font-normal text-sm items-center rounded-md border-t py-3.5 lg:py-5"
        >
          <span>Recipient Details</span>
          <span
            class="text-primary font-semibold text-2xl cursor-pointer"
            @click="toggleShow('showRecipientDetails')"
            >{{ showRecipientDetails ? '-' : '+' }}</span
          >
        </li>
        <transition name="slide-fade">
          <div v-if="showRecipientDetails">
            <!-- bank details -->
            <div
              v-if="transactionInfo.transferDestination.bankDetails"
              class="pt-3"
            >
              <div
                v-if="
                  valueExists(
                    transactionInfo.transferDestination.bankDetails.bankName
                  )
                "
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Bank Name</p>
                <p class="text-sm font-semibold">
                  {{
                    transactionInfo.transferDestination.bankDetails.bankName ||
                    ''
                  }}
                  <span
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="
                      transactionInfo.transferDestination.bankDetails.bankName
                    "
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </span>
                </p>
              </div>
              <div
                v-if="
                  valueExists(
                    transactionInfo.transferDestination.bankDetails
                      .accountNumber
                  )
                "
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Account Number</p>
                <p class="text-sm font-semibold">
                  {{
                    transactionInfo.transferDestination.bankDetails
                      .accountNumber || ''
                  }}
                  <span
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="
                      transactionInfo.transferDestination.bankDetails
                        .accountNumber
                    "
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </span>
                </p>
              </div>
              <div
                v-if="
                  valueExists(
                    transactionInfo.transferDestination.bankDetails.accountName
                  )
                "
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Account Name</p>
                <p class="text-sm font-semibold">
                  {{
                    transactionInfo.transferDestination.bankDetails
                      .accountName || ''
                  }}
                  <span
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="
                      transactionInfo.transferDestination.bankDetails
                        .accountName
                    "
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </span>
                </p>
              </div>
              <div
                v-if="valueExists(transactionInfo.bankPhone)"
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Bank Phone</p>
                <p class="text-sm font-semibold">
                  {{ transactionInfo.bankPhone || '-' }}
                  <span
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="transactionDetails.bankNo"
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </span>
                </p>
              </div>
              <div
                v-if="
                  valueExists(
                    transactionInfo.transferDestination.bankDetails.bankAddress
                  )
                "
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Bank Address</p>
                <p class="text-sm font-semibold">
                  {{
                    transactionInfo.transferDestination.bankDetails
                      .bankAddress || ''
                  }}
                  <span
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="
                      transactionInfo.transferDestination.bankDetails
                        .bankAddress
                    "
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </span>
                </p>
              </div>
              <div
                v-if="valueExists(transactionInfo.beneficiaryAddress)"
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Beneficiary Address</p>
                <p class="text-sm font-semibold" style="word-break: normal">
                  {{ transactionInfo.beneficiaryAddress || '-' }}
                  <span
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="transactionInfo.beneficiaryAddress"
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </span>
                </p>
              </div>
              <div
                v-if="valueExists(transactionInfo.depositReference)"
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Reference</p>
                <p class="text-sm font-bold">
                  {{ transactionInfo.depositReference || '' }}
                  <span
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="transactionInfo.depositReference"
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </span>
                </p>
              </div>
              <div
                v-if="
                  valueExists(
                    transactionInfo.transferDestination.bankDetails
                      .routingNumber
                  )
                "
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Routing Number</p>
                <p class="text-sm font-semibold">
                  {{
                    transactionInfo.transferDestination.bankDetails
                      .routingNumber || ''
                  }}
                  <span
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="
                      transactionInfo.transferDestination.bankDetails
                        .routingNumber
                    "
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </span>
                </p>
              </div>
            </div>
            <!-- wallet details -->
            <div
              v-if="transactionInfo.transferDestination.walletDetails"
              class="pt-3"
            >
              <div
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Asset Type</p>
                <p class="text-sm font-semibold">
                  {{
                    transactionInfo.transferDestination.walletDetails
                      .assetType || ''
                  }}
                  <span
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="
                      transactionInfo.transferDestination.walletDetails
                        .assetType
                    "
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </span>
                </p>
              </div>
              <div
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Network</p>
                <p class="text-sm font-semibold">
                  {{
                    transactionInfo.transferDestination.walletDetails.network ||
                    ''
                  }}
                  <span
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="
                      transactionInfo.transferDestination.walletDetails.network
                    "
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </span>
                </p>
              </div>
              <div
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Wallet Address</p>
                <p class="text-sm font-semibold overflow-scroll">
                  {{
                    transactionInfo.transferDestination.walletDetails
                      .walletAddress || ''
                  }}
                  <span
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="
                      transactionInfo.transferDestination.walletDetails
                        .walletAddress
                    "
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </span>
                </p>
              </div>
            </div>
          </div>
        </transition>
        <li
          class="flex justify-between font-normal text-sm items-center rounded-md border-t py-3.5 lg:py-5"
          v-if="transactionInfo.transferDestination.intermediaryBank"
        >
          <span>Intermediary Bank</span>
          <span
            class="text-primary font-semibold text-2xl cursor-pointer"
            @click="toggleShow('showIntermediaryBankDetails')"
            >{{ showIntermediaryBankDetails ? '-' : '+' }}</span
          >
        </li>
        <transition name="slide-fade">
          <div v-if="showIntermediaryBankDetails">
            <div class="pt-3">
              <div
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Country</p>
                <p class="text-sm font-semibold">
                  {{
                    transactionInfo.transferDestination.intermediaryBank || '-'
                  }}
                </p>
              </div>
              <div
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Bank Name</p>
                <p class="text-sm font-semibold">
                  {{
                    transactionInfo.transferDestination.intermediaryBank || '-'
                  }}
                </p>
              </div>
              <div
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Account Number</p>
                <p class="text-sm font-semibold">
                  {{
                    transactionInfo.transferDestination.intermediaryBank || '-'
                  }}
                </p>
              </div>
              <div
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Account Name</p>
                <p class="text-sm font-semibold">
                  {{
                    transactionInfo.transferDestination.intermediaryBank || '-'
                  }}
                </p>
              </div>
              <div
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Recipient Email</p>
                <p class="text-sm font-bold">
                  {{
                    transactionInfo.transferDestination.intermediaryBank || '-'
                  }}
                </p>
              </div>
            </div>
          </div>
        </transition>
      </ul>
    </div>
    <div v-if="isCompletedTransaction">
      <button
        class="w-full flex justify-center gap-x-2 bg-green-6 text-green-1 border border-green-1 py-4 rounded-lg"
        @click="generateReceipt"
      >
        <download-icon />
        {{ isLoading ? 'Downloading...' : 'Download Receipt' }}
      </button>
    </div>
    <vuehtml2pdf
      :manual-pagination="true"
      :filename="fileName"
      :pdf-quality="2"
      pdf-format="a4"
      pdf-content-width="800px"
      :enable-download="true"
      ref="DownloadComp"
      :show-layout="false"
    >
      <section slot="pdf-content">
        <generate-receipt-details :transferType="'Transfer'" />
      </section>
    </vuehtml2pdf>
  </section>
</template>
<script>
import {
  AddComma,
  ParseLocalTime,
  ParseMoneyAll,
  ParseMoney,
  GetCurrencyCountry,
  MaskWalletAddressTwo,
  ConvertValueToXDP,
  StatusClass,
  FormatServerName
} from '@/utils/helpers.js'
import { LOCAL_COUNTRIES } from '@/utils/constants'
import getReceiptDetails from '@/mixins/global.js'
import userKinds from '@/mixins/userKinds.js'
import app from '@/mixins/app.js'

export default {
  mixins: [getReceiptDetails, userKinds, app],
  props: {
    transactionDetails: {
      type: Object,
      default: () => {}
    },
    rateIncreased: {
      type: Boolean,
      default: () => false,
      required: true
    }
  },
  data () {
    return {
      showReasonForTransfer: false,
      showDepositDetails: false,
      showExchangeDetails: false,
      showRecipientDetails: false,
      showIntermediaryBankDetails: false,
      fxDepositDetailsType: false,
      payload: {
        id: ''
      },
      isLoading: false
    }
  },
  methods: {
    valueExists (val) {
      return val && val.length > 0
    },
    formatServerName (name) {
      return FormatServerName(name)
    },
    statusClass (status) {
      return StatusClass(status)
    },
    handleClose () {
      this.$emit('close')
    },
    convertValueTo2DP (val) {
      return ConvertValueToXDP(val, 2)
    },
    toggleShow (arg) {
      this[arg] = !this[arg]
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    addComma (val) {
      return AddComma(val.toString())
    },
    ParseLocalTime (time) {
      return ParseLocalTime(time)
    },
    maskID (val) {
      return MaskWalletAddressTwo(val)
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Copied'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    showDepositDetailsHandler () {
      this.showDepositDetails = !this.showDepositDetails
    },
    parseMoney (amount) {
      return ParseMoney(amount)
    },
    parseLocalTime (time) {
      return ParseLocalTime(time)
    },
    generateReceipt () {
      this.payload.id = this.transactionInfo.transferID
      this.getReceiptDetails(this.payload)
    }
  },
  components: {
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue'),
    Transition: () =>
      import('@/components/v2/animation/transition/Transition.vue'),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    WarningIcon: () => import('@/assets/icons/WarningIcon.vue'),
    DownloadIcon: () => import('@/assets/v2/icons/CloudDownloadIcon.vue'),
    HoldingsHistoryPage: () =>
      import(
        '@/components/v2/terminal/drawers/transactionDrawer/HoldingsHistoryPage.vue'
      ),
    GenerateReceiptDetails: () =>
      import('@/components/common/GenerateReceiptDetails.vue')
  },
  computed: {
    isHoldingsTransfer () {
      return this.transactionInfo.transferSource === 'treasury'
    },
    isStatusHistory () {
      return (
        this.transactionInfo.transferStatus === this.$STATUS.COMPLETED ||
        this.transactionInfo.transferStatus === this.$STATUS.FAILED ||
        this.transactionInfo.transferStatus === this.$STATUS.CANCELLED
      )
    },
    transactionInfo () {
      return this.$store.state.transactions.transactionDetails
    },
    displayTimeDate () {
      const timeToPick = this.isStatusHistory
        ? this.transactionInfo.transferUpdatedAt
        : this.transactionInfo.transferCreatedAt

      return this.tooltipDateContent(timeToPick)
    },
    isLocalTransferDetails () {
      return LOCAL_COUNTRIES.find(
        (country) =>
          country.currency === this.transactionInfo.transferInstruction.from
      )
    },
    localDepositDetails () {
      return [
        {
          label: 'Bank Name',
          value:
            this.transactionInfo.transferInstruction?.account_details
              ?.bankName || ''
        },
        {
          label: 'Account Number',
          value:
            this.transactionInfo.transferInstruction?.account_details
              ?.accountNumber || ''
        },
        {
          label: 'Account Name',
          value:
            this.transactionInfo.transferInstruction?.account_details
              ?.accountName || ''
        },
        {
          label: 'Reference',
          value: this.transactionInfo?.depositReference || ''
        }
      ]
    },
    fxTransferDepositDetails () {
      const detailsType = this.fxDepositDetailsType
        ? 'wireInstruction'
        : 'wireInstructionIntl'
      return [
        {
          label: 'Account Name',
          value:
            this.transactionInfo.fxDepositDetails?.account_details[detailsType]
              ?.accountName || ''
        },
        {
          label: 'Account Number',
          value:
            this.transactionInfo.fxDepositDetails?.account_details[detailsType]
              ?.accountNumber || ''
        },
        {
          label: 'Depository Bank Name',
          value:
            this.transactionInfo.fxDepositDetails?.account_details[detailsType]
              ?.depositoryBankName || ''
        },
        {
          label: 'Bank Address',
          value:
            this.transactionInfo.fxDepositDetails?.account_details[detailsType]
              ?.bankAddress || ''
        },
        {
          label: 'Bank Phone',
          value:
            this.transactionInfo.fxDepositDetails?.account_details[detailsType]
              ?.bankPhone || ''
        },
        {
          label: 'Credit To',
          value:
            this.transactionInfo.fxDepositDetails?.account_details[detailsType]
              ?.creditTo || ''
        },
        {
          label: 'Iban',
          value:
            this.transactionInfo.fxDepositDetails?.account_details[detailsType]
              ?.iban || ''
        },
        {
          label: 'Sort Code',
          value:
            this.transactionInfo.fxDepositDetails?.account_details[detailsType]
              ?.sortCode || ''
        },
        {
          label: 'Reference',
          value:
            this.transactionInfo.fxDepositDetails?.account_details[detailsType]
              ?.reference || ''
        },
        {
          label: 'Routing Number',
          value:
            this.transactionInfo.fxDepositDetails?.account_details[detailsType]
              ?.routingNumber || ''
        },
        {
          label: 'Swift Code',
          value:
            this.transactionInfo.fxDepositDetails?.account_details[detailsType]
              ?.swiftCode || ''
        }
      ]
    },
    isCompletedTransaction () {
      return this.transactionInfo.transferStatus === this.$STATUS.COMPLETED
    },
    fileName () {
      return `${this.businessName || this.adminBusinessName} - (${
        this.transactionInfo.transferAmount.amount
      } ${this.transactionInfo.transferAmount.currency})`
    }
  }
}
</script>
<style scoped>
>>> .el-switch .el-switch__core {
  border-color: var(--color-primary);
  background-color: var(--color-primary);
}
</style>
